import { createContext, useContext } from "react";

import { AuthCallbackPath } from "./callback-paths";

export interface ConnectionFlowsConfig {
  /** `:customerSlug`-templated connectors endpoint (properties + connections). */
  connectorsApiUrl: string;
  /** `:customerSlug`-templated complete-connection endpoint (post-OAuth account/property selection). */
  completeConnectionApiUrl: string;
  /** `:customerSlug`-templated endpoint to submit (POST) a completed connection. */
  completeConnectionSubmitApiUrl: string;
  /** `:customerSlug`-templated endpoint to submit (POST) a cancelled connection. */
  cancelConnectionSubmitApiUrl: string;
  /** Callback path a connector returns to after OAuth when a flow doesn't pass its own. */
  defaultCallbackPath: AuthCallbackPath;
  /**
   * Whether this surface supports self-serve reconnection of a broken connection. When `false`,
   * `ConnectionsList` shows every connection as connected and never renders a Reconnect
   * action/form.
   */
  supportsReconnection: boolean;
}

/**
 * Defaults target the funding-application onboarding surface so existing usages keep working
 * without a provider. Other surfaces (e.g. the profile settings "Manage connections" page) wrap
 * their flows in `<ConnectionFlowsProvider>` with their own endpoints, callback path, and
 * reconnection behavior.
 *
 * Keep these URLs in sync with `FUNDING_APPLICATION_CONNECTORS_API` /
 * `FUNDING_APPLICATION_COMPLETE_CONNECTION_API` / `FUNDING_APPLICATION_COMPLETE_CONNECTION_SUBMIT_API` /
 * `FUNDING_APPLICATION_CANCEL_CONNECTION_SUBMIT_API` in funding-application.
 *
 * `supportsReconnection` is `false` here because onboarding doesn't yet support the reconnect UX.
 */
const DEFAULT_CONFIG: ConnectionFlowsConfig = {
  connectorsApiUrl: "/api/:customerSlug/funding/api/connectors/",
  completeConnectionApiUrl: "/api/:customerSlug/funding/api/connectors/complete-connection",
  completeConnectionSubmitApiUrl: "/api/:customerSlug/getting-started/complete-connection",
  cancelConnectionSubmitApiUrl: "/api/:customerSlug/getting-started/cancel-connection",
  defaultCallbackPath: AuthCallbackPath.FUNDING_APPLICATION_ADD_CONNECTION,
  supportsReconnection: false,
};

export const ConnectionFlowsConfigContext = createContext<ConnectionFlowsConfig>(DEFAULT_CONFIG);

export const useConnectionFlowsConfig = (): ConnectionFlowsConfig => useContext(ConnectionFlowsConfigContext);
